import React from 'react';
import PropTypes from 'prop-types';

import { Badge, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import dayjs from 'dayjs';

import { DATE_FORMAT_WITH_TIME } from 'lib/constants';
import {
  white,
  shadowMedium,
  backgroundGrey1,
  greyBorder,
  iconGrey,
  lighterGrey,
  supplierHeaderBg,
  supplierHeaderText,
  brandHeaderBg,
  brandHeaderText,
  idHeaderBg,
  idHeaderText,
  metaHeaderBg,
  metaHeaderText,
  supplierCellBg,
  brandCellBg,
  idCellBg,
  metaCellBg,
} from 'lib/css/colors';
import pointerOnHover from 'lib/css/pointerOnHover';

function SupplierBrandTable({ records }) {
  const formatDate = date => {
    return date ? dayjs(date).format(DATE_FORMAT_WITH_TIME) : '-';
  };

  const renderTextCell = (value, cellStyle, maxWidth = '180px') => {
    const displayValue = value || '';
    const isEmpty = !value || value === '';

    return (
      <div className={css(cellStyle)} title={displayValue} style={{ maxWidth }}>
        <span className={isEmpty ? css(styles.emptyValue) : ''}>
          {isEmpty ? '—' : displayValue}
        </span>
      </div>
    );
  };

  const renderIdCell = (value, label) => {
    return (
      <div className={css(styles.idCell)}>
        {value ? (
          <div>
            <small className="text-muted d-block">{label}</small>
            <strong>{value}</strong>
          </div>
        ) : (
          <span className={css(styles.emptyValue)}>—</span>
        )}
      </div>
    );
  };

  const getSupplierTypeBadge = type => {
    if (!type) return <span className={css(styles.emptyValue)}>—</span>;

    const variant =
      {
        Manufacturer: 'primary',
        Importer: 'info',
        'Distributor+Brand-Owner': 'success',
        Unidentified: 'secondary',
        'Broker - Not Product Authority': 'warning',
        'Re-Distributor - Not Product Authority': 'warning',
      }[type] || 'secondary';

    return (
      <Badge variant={variant} className={css(styles.supplierTypeBadge)}>
        {type}
      </Badge>
    );
  };

  return (
    <div className={css(styles.tableContainer)}>
      <div className="table-responsive">
        <Table striped hover className={css(styles.table)}>
          <thead>
            {/* Column Group Headers */}
            <tr className={css(styles.headerGroup)}>
              <th colSpan="4" className={css(styles.supplierHeader)}>
                SUPPLIER INFORMATION
              </th>
              <th colSpan="2" className={css(styles.brandHeader)}>
                BRAND INFORMATION
              </th>
              <th colSpan="4" className={css(styles.idHeader)}>
                IDENTIFIERS
              </th>
              <th colSpan="2" className={css(styles.metaHeader)}>
                METADATA
              </th>
            </tr>
            {/* Column Headers */}
            <tr>
              <th className={css(styles.supplierHeader)} style={{ minWidth: '140px' }}>
                Core Cleansed
              </th>
              <th className={css(styles.supplierHeader)} style={{ minWidth: '140px' }}>
                Proper Cleansed
              </th>
              <th className={css(styles.supplierHeader)} style={{ minWidth: '140px' }}>
                Originating Name
              </th>
              <th className={css(styles.supplierHeader)} style={{ minWidth: '120px' }}>
                Type
              </th>
              <th className={css(styles.brandHeader)} style={{ minWidth: '140px' }}>
                Core Cleansed
              </th>
              <th className={css(styles.brandHeader)} style={{ minWidth: '140px' }}>
                Originating Name
              </th>
              <th className={css(styles.idHeader)} style={{ minWidth: '80px' }}>
                VSID
              </th>
              <th className={css(styles.idHeader)} style={{ minWidth: '80px' }}>
                VSBID
              </th>
              <th className={css(styles.idHeader)} style={{ minWidth: '100px' }}>
                VSBID9
              </th>
              <th className={css(styles.idHeader)} style={{ minWidth: '90px' }}>
                FSA Brand
              </th>
              <th className={css(styles.metaHeader)} style={{ minWidth: '130px' }}>
                Entry Date
              </th>
              <th className={css(styles.metaHeader)} style={{ minWidth: '130px' }}>
                Created
              </th>
            </tr>
          </thead>
          <tbody>
            {records.length > 0 ? (
              records.map((record, index) => (
                <tr key={record.id || index} className={css(styles.hoverRow)}>
                  {/* Supplier Information */}
                  <td>
                    {renderTextCell(record.supplier_core_cleansed, styles.supplierCell, '140px')}
                  </td>
                  <td>
                    {renderTextCell(record.supplier_proper_cleansed, styles.supplierCell, '140px')}
                  </td>
                  <td>
                    {renderTextCell(
                      record.originating_supplier_names,
                      styles.supplierCell,
                      '140px',
                    )}
                  </td>
                  <td className={css(styles.supplierCell)} style={{ textAlign: 'center' }}>
                    {getSupplierTypeBadge(record.supplier_type)}
                  </td>

                  {/* Brand Information */}
                  <td>{renderTextCell(record.brand_core_cleansed, styles.brandCell, '140px')}</td>
                  <td>
                    {renderTextCell(record.originating_brand_names, styles.brandCell, '140px')}
                  </td>

                  {/* ID Information */}
                  <td>{renderIdCell(record.verified_supplier_id, 'VSID')}</td>
                  <td>{renderIdCell(record.verified_subordinate_brand_id, 'VSBID')}</td>
                  <td>{renderIdCell(record.verified_combined_supplier_brand_id, 'VSBID9')}</td>
                  <td
                    className={record.fsa_brand_id ? css(styles.clickableCell) : css(styles.idCell)}
                    onClick={
                      record.fsa_brand_id
                        ? () => {
                            window.open(
                              `/enrich-products/brand-details/${record.fsa_brand_id}`,
                              '_blank',
                            );
                          }
                        : undefined
                    }
                  >
                    {renderIdCell(record.fsa_brand_id, 'FSA')}
                  </td>

                  {/* Metadata */}
                  <td className={css(styles.metaCell)} style={{ textAlign: 'center' }}>
                    <small>{formatDate(record.entry_date_of_record)}</small>
                  </td>
                  <td className={css(styles.metaCell)} style={{ textAlign: 'center' }}>
                    <small>{formatDate(record.date_created)}</small>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="text-center text-muted py-5">
                  <div style={{ padding: '2rem' }}>
                    <i className="fas fa-inbox fa-2x mb-3 text-muted"></i>
                    <div>No records found for the given criteria</div>
                    <small className="text-muted">Try adjusting your search terms</small>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  tableContainer: {
    backgroundColor: white,
    borderRadius: '8px',
    boxShadow: `0 2px 8px ${shadowMedium}`,
    overflow: 'hidden',
  },
  table: {
    marginBottom: 0,
    fontSize: '13px',
  },
  headerGroup: {
    backgroundColor: backgroundGrey1,
    borderBottom: `2px solid ${greyBorder}`,
  },
  supplierHeader: {
    backgroundColor: supplierHeaderBg, // Original: '#e3f2fd'
    color: supplierHeaderText, // Original: '#1565c0'
    fontWeight: '600',
    fontSize: '12px',
    textAlign: 'center',
  },
  brandHeader: {
    backgroundColor: brandHeaderBg, // Original: '#f3e5f5'
    color: brandHeaderText, // Original: '#7b1fa2'
    fontWeight: '600',
    fontSize: '12px',
    textAlign: 'center',
  },
  idHeader: {
    backgroundColor: idHeaderBg, // Original: '#fff3e0'
    color: idHeaderText, // Original: '#ef6c00'
    fontWeight: '600',
    fontSize: '12px',
    textAlign: 'center',
  },
  metaHeader: {
    backgroundColor: metaHeaderBg, // Original: '#e8f5e8'
    color: metaHeaderText, // Original: '#2e7d32'
    fontWeight: '600',
    fontSize: '12px',
    textAlign: 'center',
  },
  cellContent: {
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '8px 6px',
  },
  supplierCell: {
    backgroundColor: supplierCellBg, // Original: '#fafffe'
    borderLeft: `3px solid ${supplierHeaderBg}`,
  },
  brandCell: {
    backgroundColor: brandCellBg, // Original: '#fdfaff'
    borderLeft: `3px solid ${brandHeaderBg}`,
  },
  idCell: {
    backgroundColor: idCellBg, // Original: '#fffef7'
    textAlign: 'center',
    fontWeight: '500',
    color: idHeaderText,
    borderLeft: `3px solid ${idHeaderBg}`,
  },
  metaCell: {
    backgroundColor: metaCellBg, // Original: '#f9fff9'
    borderLeft: `3px solid ${metaHeaderBg}`,
  },
  emptyValue: {
    color: iconGrey,
    fontStyle: 'italic',
  },
  supplierTypeBadge: {
    fontSize: '11px',
    padding: '4px 8px',
  },
  hoverRow: {
    ':hover': {
      backgroundColor: lighterGrey,
    },
  },
  clickableCell: {
    cursor: 'pointer',
  },
});

SupplierBrandTable.propTypes = {
  records: PropTypes.array.isRequired,
};

export default SupplierBrandTable;
